import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { ErrorResponse } from "../../../Models/Error.ts";
import {
  convertUserToCreate,
  convertUserToUpdate,
  defaultUser,
  User,
  validate,
} from "../../../Models/User.ts";
import { UsersService } from "../../../services/user/users.ts";
import { CompaniesService } from "../../../services/registry/companies.ts";
import {
  callErrorToast,
  callSuccessToast,
  cleanObject,
  getAllTimezones,
  removeError,
} from "../../../utilities/utilities.ts";
import { useAppContext } from "../../../AppProvider.jsx";
import { Company } from "../../../Models/Company.ts";

const usersService = new UsersService();
const companiesService = new CompaniesService();

interface HandlerUserProviderProps {
  id?: string;
  children: ReactNode;
  callback?: () => void;
  autosave: boolean;
}

interface HandlerUserContextValue {
  data: Partial<User>;
  error: ErrorResponse<User> | null;
  loader: boolean;
  callback?: () => void;
  edit: (user: User, property: keyof User, save?: boolean) => void;
  onSubmit: (id?: string) => void;
}

const HandlerUserContext = createContext<HandlerUserContextValue | undefined>(
  undefined
);

const HandlerUserProvider: React.FC<HandlerUserProviderProps> = ({
  id,
  children,
  autosave,
  callback,
}) => {
  const { dictionary } = useAppContext();
  const [data, setData] = useState<Partial<User>>(defaultUser);
  const [error, setError] = useState<ErrorResponse<User> | null>(null);
  const [loader, setLoader] = useState<boolean>(false);

  const prevError = useRef<ErrorResponse<User>>(null);
  const prevOwner = useRef<Company | undefined>(null);

  /**
   * Fetches user details by ID.
   * @param id - The user ID.
   */
  const getUser = (id: string): void => {
    setLoader(true);
    usersService
      .getUser(id)
      .then((res) => {
        const timezones = getAllTimezones();
        const data: User = {
          ...res.data,
          timeZone: timezones.find((o: any) => o.value === res.data?.timeZone),
        };
        prevOwner.current = data.owner;
        setData(data);
        setLoader(false);
      })
      .catch((err) => {
        callErrorToast(err);
        setLoader(false);
        setError(err);
      });
  };

  const getAvailableRoles = (ownerId: string): void => {
    companiesService
      .getAvailableRoles(ownerId)
      .then((res) => {
        if (res.data) {
          res.data = res.data.map((r: string) => {
            return { code: r };
          });
          const user = { ...data, roles: [res.data[res.data.length - 1]] };
          setData(user);
        }
      })
      .catch((err) => {
        callErrorToast(err);
        setError(err);
      });
  };

  const create = (user: User): void => {
    const validation = validate(data as User);
    if (!validation.isValid) {
      setError({ response: { data: validation.errors } });
      setLoader(false);
      return;
    }
    const userToCreate = convertUserToCreate(user);
    usersService
      .create(userToCreate)
      .then((res) => {
        setData(res.data);
        setLoader(false);
        callSuccessToast(dictionary.messages.save_successful);
        if (callback) callback();
      })
      .catch((err) => {
        setError(err);
        callErrorToast(err);
        setLoader(false);
      });
  };

  const edit = (user: User, property: keyof User | null, save = true): void => {
    user = cleanObject(user);
    if (property) {
      removeError(property, error, prevError, setError);
    }

    if (save && autosave) {
      const validation = validate(data as User);
      if (!validation.isValid) {
        setError({ response: { data: validation.errors } });
        setLoader(false);
        return;
      }

      const userUpdated = convertUserToUpdate(user);
      usersService
        .edit(userUpdated)
        .then(() => {
          setData(user);
          if (save && callback) {
            callback();
          }
          callSuccessToast(dictionary.messages.save_successful);
        })
        .catch((err) => {
          setError(err);
          callErrorToast(err);
          setLoader(false);
        });
      return;
    }
    setData(user);
  };

  const onSubmit = (id?: string): void => {
    setLoader(true);
    if (id) {
      edit(data as User, null);
      return;
    }
    create(data as User);
  };

  useEffect(() => {
    if (id) {
      getUser(id);
    }
  }, [id]);

  useEffect(() => {
    if (data.owner && prevOwner?.current !== data?.owner) {
      getAvailableRoles(data?.owner.id);
    }
  }, [data.owner]);

  return (
    <HandlerUserContext.Provider
      value={{
        data,
        error,
        loader,
        edit,
        onSubmit,
        callback,
      }}
    >
      {children}
    </HandlerUserContext.Provider>
  );
};

const useHandlerContext = (): HandlerUserContextValue => {
  const context = useContext(HandlerUserContext);
  if (!context) {
    throw new Error(
      "useHandlerContext must be used within a HandlerUserProvider"
    );
  }
  return context;
};

export { HandlerUserProvider, useHandlerContext };
